import { motion } from 'framer-motion';
import {
  FiActivity,
  FiCode,
  FiCpu,
  FiDatabase,
  FiGitBranch,
  FiLayers,
  FiLayout,
  FiServer,
  FiShield
} from 'react-icons/fi';
import { FaChartArea, FaChartLine, FaFeatherAlt, FaJava, FaNetworkWired } from 'react-icons/fa';
import { VscVscode } from 'react-icons/vsc';
import {
  SiBootstrap,
  SiCanva,
  SiCss3,
  SiDocker,
  SiExpress,
  SiFigma,
  SiGit,
  SiGithub,
  SiGithubactions,
  SiHtml5,
  SiHibernate,
  SiJavascript,
  SiJquery,
  SiJupyter,
  SiKeras,
  SiMongodb,
  SiMysql,
  SiNetlify,
  SiNextdotjs,
  SiNodedotjs,
  SiNumpy,
  SiPandas,
  SiPostgresql,
  SiPostman,
  SiPycharm,
  SiPython,
  SiReact,
  SiScikitlearn,
  SiSpring,
  SiSpringsecurity,
  SiTailwindcss,
  SiTensorflow,
  SiTypescript,
  SiVercel,
  SiSwagger
} from 'react-icons/si';

const skillIcons = {
  Java: FaJava,
  'Spring Boot': SiSpring,
  'Spring Security': SiSpringsecurity,
  'Hibernate / JPA': SiHibernate,
  'Node.js': SiNodedotjs,
  'Express.js': SiExpress,
  'REST APIs': FaNetworkWired,
  'JWT / Auth': FiShield,
  PostgreSQL: SiPostgresql,
  MySQL: SiMysql,
  MongoDB: SiMongodb,
  'Swagger / OpenAPI': SiSwagger,
  'GitHub Actions': SiGithubactions,
  Docker: SiDocker,
  Git: SiGit,
  HTML: SiHtml5,
  CSS: SiCss3,
  JavaScript: SiJavascript,
  TypeScript: SiTypescript,
  'React.js': SiReact,
  'Next.js': SiNextdotjs,
  'Tailwind CSS': SiTailwindcss,
  Bootstrap: SiBootstrap,
  jQuery: SiJquery,
  Python: SiPython,
  Pandas: SiPandas,
  NumPy: SiNumpy,
  Matplotlib: FaChartLine,
  Seaborn: FaChartArea,
  'Scikit-Learn': SiScikitlearn,
  TensorFlow: SiTensorflow,
  Keras: SiKeras,
  GitHub: SiGithub,
  Maven: FaFeatherAlt,
  Postman: SiPostman,
  Vercel: SiVercel,
  Netlify: SiNetlify,
  'VS Code': VscVscode,
  PyCharm: SiPycharm,
  'Jupyter Notebook': SiJupyter,
  Figma: SiFigma,
  Canva: SiCanva,
  'Data structures & algorithms': FiCode,
  'System design': FiLayers,
  'Database management': FiDatabase,
  'Object-oriented programming': FiCpu,
  'Design patterns': FiLayers,
  'Software development lifecycle': FiGitBranch,
  'Agile / Scrum': FiGitBranch,
  'Software testing lifecycle': FiActivity,
  'Computer architecture': FiCpu,
  'Computer networks': FiGitBranch,
  'Software engineering': FiCode
};

const skillIconColors = {
  Java: '#e76f00',
  'Spring Boot': '#6db33f',
  'Spring Security': '#6db33f',
  'Hibernate / JPA': '#59666c',
  'Node.js': '#5fa04e',
  'Express.js': '#555555',
  'REST APIs': '#2496ed',
  PostgreSQL: '#4169e1',
  MySQL: '#4479a1',
  MongoDB: '#47a248',
  'Swagger / OpenAPI': '#85ea2d',
  'GitHub Actions': '#2088ff',
  Docker: '#2496ed',
  Git: '#f05032',
  HTML: '#e34f26',
  CSS: '#1572b6',
  JavaScript: '#f7df1e',
  TypeScript: '#3178c6',
  'React.js': '#61dafb',
  'Next.js': '#111827',
  'Tailwind CSS': '#06b6d4',
  Bootstrap: '#7952b3',
  jQuery: '#0769ad',
  Python: '#3776ab',
  Pandas: '#150458',
  NumPy: '#4dabcf',
  Matplotlib: '#11557c',
  Seaborn: '#4c72b0',
  'Scikit-Learn': '#f7931e',
  TensorFlow: '#ff6f00',
  Keras: '#d00000',
  GitHub: '#24292f',
  Maven: '#c71a36',
  Postman: '#ff6c37',
  Vercel: '#111827',
  Netlify: '#00ad9f',
  'VS Code': '#007acc',
  PyCharm: '#21d789',
  'Jupyter Notebook': '#f37626',
  Figma: '#f24e1e',
  Canva: '#00c4cc'
};

const skillGroups = [
  {
    title: 'Frontend & product UI',
    description: 'Building clear, responsive interfaces.',
    icon: FiLayout,
    skills: ['HTML', 'CSS', 'JavaScript', 'TypeScript', 'React.js', 'Next.js', 'Tailwind CSS', 'Bootstrap', 'jQuery'],
    accent: 'indigo'
  },
  {
    title: 'Data & machine learning',
    description: 'Tools for analysis, visualization, and ML.',
    icon: FiActivity,
    skills: ['Python', 'Pandas', 'NumPy', 'Matplotlib', 'Seaborn', 'Scikit-Learn', 'TensorFlow', 'Keras'],
    accent: 'cyan'
  },
  {
    title: 'Delivery & developer tools',
    description: 'Version control, testing, and deployment workflow.',
    icon: FiGitBranch,
    skills: ['Git', 'GitHub', 'GitHub Actions', 'Docker', 'Maven', 'Postman', 'Vercel', 'Netlify', 'VS Code', 'PyCharm', 'Jupyter Notebook', 'Figma', 'Canva'],
    accent: 'violet'
  }
];

const foundations = [
  'Data structures & algorithms',
  'System design',
  'Database management',
  'Object-oriented programming',
  'Design patterns',
  'Software development lifecycle',
  'Agile / Scrum',
  'Software testing lifecycle',
  'Computer architecture',
  'Computer networks',
  'Software engineering'
];

function SkillPills({ skills, featured = false }) {
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Skills">
      {skills.map((skill, index) => (
        (() => {
          const Icon = skillIcons[skill] || FiCode;
          return (
        <motion.li
          key={skill}
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.25, delay: Math.min(index * 0.025, 0.2) }}
          className={featured
            ? 'inline-flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/75 px-3 py-1.5 text-[15px] font-medium text-indigo-900 shadow-sm dark:border-indigo-300/15 dark:bg-white/[0.07] dark:text-indigo-100'
            : 'inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/80 px-3 py-1.5 text-[15px] font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200'}
        >
          <Icon
            size={17}
            aria-hidden="true"
            className={`shrink-0 ${skillIconColors[skill] ? '' : 'text-indigo-500 dark:text-indigo-300'}`}
            style={skillIconColors[skill] ? { color: skillIconColors[skill] } : undefined}
          />
          <span>{skill}</span>
        </motion.li>
          );
        })()
      ))}
    </ul>
  );
}

function SkillGroup({ title, description, icon: Icon, skills, accent }) {
  const accents = {
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300',
    cyan: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-400/10 dark:text-cyan-300',
    violet: 'bg-violet-100 text-violet-700 dark:bg-violet-400/10 dark:text-violet-300'
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45 }}
      className="h-full rounded-3xl border border-slate-200/80 bg-white/80 p-5 shadow-[0_12px_34px_rgba(30,41,59,0.055)] backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-900/70 sm:p-6 lg:col-span-4"
    >
      <div className="mb-4 flex items-start gap-3">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${accents[accent]}`}>
          <Icon size={19} aria-hidden="true" />
        </span>
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{description}</p>
        </div>
      </div>
      <SkillPills skills={skills} />
    </motion.article>
  );
}

export default function Skills() {
  return (
    <section id="skills" className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-indigo-50/40 to-white px-6 py-20 dark:from-gray-950 dark:via-slate-950 dark:to-gray-900 md:px-20">
      <div aria-hidden="true" className="pointer-events-none absolute -right-36 top-12 h-96 w-96 rounded-full bg-indigo-200/25 blur-3xl dark:bg-indigo-700/10" />
      <div className="relative mx-auto max-w-6xl">
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="mb-10 text-center"
        >
          <p className="section-eyebrow">Technical toolkit</p>
          <h2 className="mb-3 text-4xl font-bold text-slate-900 dark:text-white">
            <span className="border-b-4 border-indigo-500 pb-1">Skills</span>
          </h2>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
            A backend-first toolkit shaped by production applications and full-stack delivery.
          </p>
        </motion.header>

        <div className="grid gap-5 lg:grid-cols-12">
          <motion.article
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-3xl border border-indigo-200/70 bg-gradient-to-br from-white via-indigo-50/80 to-violet-100/60 p-6 shadow-[0_18px_45px_rgba(79,70,229,0.1)] dark:border-indigo-300/15 dark:from-slate-900 dark:via-indigo-950/55 dark:to-slate-900 sm:p-8 lg:col-span-7"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -right-10 -top-16 h-48 w-48 rounded-full border border-indigo-300/30 dark:border-indigo-300/10" />
            <div className="relative">
              <div className="mb-5 flex items-center justify-between gap-4">
                <span className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-sm dark:bg-indigo-400/15 dark:text-indigo-200">
                  <FiServer size={13} aria-hidden="true" /> Core focus
                </span>
                <span className="text-xs font-medium text-indigo-500 dark:text-indigo-300/70">Production-backed</span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">Backend engineering</h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600 dark:text-slate-300 sm:text-base">
                Designing secure APIs, reliable application services, and data-backed features.
              </p>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-500 dark:text-indigo-300">Java ecosystem</p>
                  <SkillPills featured skills={['Java', 'Spring Boot', 'Spring Security', 'Hibernate / JPA']} />
                </div>
                <div>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-500 dark:text-indigo-300">Services & authentication</p>
                  <SkillPills featured skills={['Node.js', 'Express.js', 'REST APIs', 'JWT / Auth']} />
                </div>
              </div>
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="rounded-3xl border border-slate-200/80 bg-white/85 p-6 shadow-[0_12px_34px_rgba(30,41,59,0.055)] backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-900/70 sm:p-7 lg:col-span-5"
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300"><FiDatabase size={19} aria-hidden="true" /></span>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Data, APIs &amp; delivery</h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Persistence, documentation, and shipping.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Databases</p>
                <SkillPills skills={['PostgreSQL', 'MySQL', 'MongoDB']} />
              </div>
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Quality &amp; deployment</p>
                <SkillPills skills={['Swagger / OpenAPI', 'GitHub Actions', 'Docker', 'Git']} />
              </div>
            </div>
          </motion.article>

          {skillGroups.map((group) => <SkillGroup key={group.title} {...group} />)}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.4 }}
          className="mt-5 rounded-3xl border border-slate-200/80 bg-white/65 p-5 dark:border-slate-700/70 dark:bg-slate-900/45 sm:p-6"
        >
          <div className="mb-3 flex items-center gap-2 text-slate-800 dark:text-slate-200">
            <FiLayers className="text-indigo-500 dark:text-indigo-300" aria-hidden="true" />
            <h3 className="text-sm font-bold">Engineering foundations</h3>
          </div>
          <SkillPills skills={foundations} />
        </motion.div>
      </div>
    </section>
  );
}
